import { useState, useEffect } from "react";
import {
  Typography,
  Button,
  IconButton,
  Paper,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

export default function FileFormatMasterGridsPage() {
  const { enqueueSnackbar } = useSnackbar();

  // -------------------------------
  // State
  // -------------------------------
  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [possessions, setPossessions] = useState([]);

  const [isChangedCategory, setIsChangedCategory] = useState(false);
  const [isChangedType, setIsChangedType] = useState(false);
  const [isChangedPossession, setIsChangedPossession] = useState(false);

  // -------------------------------
  // Fetch data
  // -------------------------------
  useEffect(() => {
    fetchCategories(setDocCategories);
    fetchTypes(setDocTypes);
    fetchPossessions(setPossessions);
  }, []);

  // -------------------------------
  // Columns
  // -------------------------------
  const categoryColumns = [
    { field: "id", headerName: "ID", width: 50 },
    {
      field: "doc_category_name",
      headerName: "Category Name",
      flex: 1,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            deleteCategory(params.row.id, setDocCategories, fetchCategories);
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const typeColumns = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
    },
    {
      field: "doc_category_id",
      headerName: "Category",
      flex: 1,
      editable: true,
      // Edit mode: dropdown select
      renderEditCell: (params) => (
        <Select
          value={params.value || ""} // use empty string if null
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: "doc_category_id",
              value: e.target.value,
            })
          }
          fullWidth
        >
          {docCategories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.doc_category_name}
            </MenuItem>
          ))}
        </Select>
      ),
      // Display mode: show category name
      renderCell: (params) => {
        if (!params.value) return ""; // handle null/empty
        const category = docCategories.find((c) => c.id === params.value);
        return category ? category.doc_category_name : "";
      },
    },
    {
      field: "doc_type",
      headerName: "Document Type",
      flex: 1,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => deleteType(params.row.id, fetchTypes, setDocTypes)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const possessionColumns = [
    { field: "id", headerName: "ID", width: 50 },
    { field: "possession", headerName: "Possession", flex: 1, editable: true },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={() =>
            deletePossession(params.row.id, fetchPossessions, setPossessions)
          }
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  // -------------------------------
  // Handlers for adding new rows
  // -------------------------------
  const handleAddCategory = () =>
    addCategory(setDocCategories, docCategories);
  const handleAddType = () =>
    addType(setDocTypes, docTypes, docCategories);
  const handleAddPossession = () =>
    addPossession(setPossessions, possessions);

  // -------------------------------
  // Handlers for saving
  // -------------------------------
  const handleSaveCategory = () =>
    saveCategories(docCategories, fetchCategories, setDocCategories);
  const handleSaveType = () =>
    saveTypes(docTypes, fetchTypes, setDocTypes);
  const handleSavePossession = () =>
    savePossessions(possessions, fetchPossessions, setPossessions);

  // -------------------------------
  // Process row update
  // -------------------------------
  const processCategoryRow = (newRow, oldRow) =>
    updateCategoryRow(newRow, docCategories, setDocCategories, setIsChangedCategory);
  const processTypeRow = (newRow, oldRow) =>
    updateTypeRow(newRow, docTypes, setDocTypes, setIsChangedType);
  const processPossessionRow = (newRow, oldRow) =>
    updatePossessionRow(newRow, possessions, setPossessions, setIsChangedPossession);

  // -------------------------------
  // File format functions
  // -------------------------------

  // fetch categories
  const fetchCategories = async (setDocCategories) => {
    try {
      const res = await ApiServices.get("file-format/categories");
      setDocCategories(res?.data?.docCategories || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // add new category
  const addCategory = (setDocCategories, docCategories) => {
    const tempId = ``;
    setDocCategories([...docCategories, { id: tempId, doc_category_name: "" }]);
  };

  // delete category
  const deleteCategory = async (id, setDocCategories, fetchCategories) => {
    try {
      const userId = JSON.parse(localStorage.getItem("user"))?.id || null;
      await ApiServices.patch(`file-format/category/delete/${id}`, {
        deleted_by: userId,
      });
      fetchCategories(setDocCategories);
      enqueueSnackbar("Category deleted successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Error deleting category!", { variant: "error" });
    }
  };

  // save categories
  const saveCategories = async (docCategories, fetchCategories, setDocCategories) => {
    try {
      await ApiServices.post("file-format/category/save", docCategories);
      fetchCategories(setDocCategories);
      setIsChangedCategory(false);
      enqueueSnackbar("Categories saved successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Error saving categories!", { variant: "error" });
    }
  };

  const updateCategoryRow = (
    newRow,
    docCategories,
    setDocCategories,
    setIsChangedCategory
  ) => {
    setIsChangedCategory(true);
    setDocCategories(
      docCategories.map((r) => (r.id === newRow.id ? newRow : r))
    );
    return newRow;
  };

  // ----------- Types -------------
  const fetchTypes = async (setDocTypes) => {
    try {
      const res = await ApiServices.get("file-format/types");
      setDocTypes(res?.data?.docTypes || []);
    } catch (err) {
      console.error("Error fetching types:", err);
    }
  };

  const addType = (setDocTypes, docTypes, docCategories) => {
    const tempId = ``;
    setDocTypes([
      ...docTypes,
      { id: tempId, doc_type: "", doc_category_id: docCategories[0]?.id || "" },
    ]);
  };

  const deleteType = async (id, fetchTypes, setDocTypes) => {
    try {
      const userId = JSON.parse(localStorage.getItem("user"))?.id || null;
      await ApiServices.patch(`file-format/type/delete/${id}`, {
        deleted_by: userId,
      });
      fetchTypes(setDocTypes);
      enqueueSnackbar("Document type deleted successfully!", {
        variant: "success"
      });
    } catch (err) {
      enqueueSnackbar("Error deleting document type!", { variant: "error" });
    }
  };

  const saveTypes = async (docTypes, fetchTypes, setDocTypes) => {
    try {
      await ApiServices.post("file-format/type/save", docTypes);
      fetchTypes(setDocTypes);
      setIsChangedType(false);
      enqueueSnackbar("Document types saved successfully!", {
        variant: "success"
      });
    } catch (err) {
      enqueueSnackbar("Error saving document types!", { variant: "error" });
    }
  };

  const updateTypeRow = (
    newRow,
    docTypes,
    setDocTypes,
    setIsChangedType
  ) => {
    setIsChangedType(true);
    setDocTypes(docTypes.map((r) => (r.id === newRow.id ? newRow : r)));
    return newRow;
  };

  // ----------- Possessions -------------
  const fetchPossessions = async (setPossessions) => {
    try {
      const res = await ApiServices.get("file-format/possessions");
      setPossessions(res?.data?.possessions || []);
    } catch (err) {
      console.error("Error fetching possessions:", err);
    }
  };

  const addPossession = (setPossessions, possessions) => {
    const tempId = ``;
    setPossessions([...possessions, { id: tempId, possession: "" }]);
  };

  const deletePossession = async (id, fetchPossessions, setPossessions) => {
    try {
      const userId = JSON.parse(localStorage.getItem("user"))?.id || null;
      await ApiServices.patch(`file-format/possession/delete/${id}`, {
        deleted_by: userId,
      });
      fetchPossessions(setPossessions);
      enqueueSnackbar("Possession deleted successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Error deleting possession!", { variant: "error" });
    }
  };

  const savePossessions = async (possessions, fetchPossessions, setPossessions) => {
    try {
      await ApiServices.post("file-format/possession/save", possessions);
      fetchPossessions(setPossessions);
      setIsChangedPossession(false);
      enqueueSnackbar("Possessions saved successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Error saving possessions!", { variant: "error" });
    }
  };

  const updatePossessionRow = (
    newRow,
    possessions,
    setPossessions,
    setIsChangedPossession
  ) => {
    setIsChangedPossession(true);
    setPossessions(possessions.map((r) => (r.id === newRow.id ? newRow : r)));
    return newRow;
  };

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6">File Format Setup</Typography>

      {/* Document Categories */}
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1">Document Categories</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
            >
              Add
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveCategory}
              disabled={!isChangedCategory}
            >
              Save
            </Button>
          </Box>
        </Box>
        <div style={{ height: 400, marginTop: 16 }}>
          <DataGrid
            rows={docCategories}
            columns={categoryColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            editMode="row"
            processRowUpdate={processCategoryRow}
            disableSelectionOnClick
          />
        </div>
      </Box>

      {/* Document Types */}
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1">Document Types</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddType}
            >
              Add
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveType}
              disabled={!isChangedType}
            >
              Save
            </Button>
          </Box>
        </Box>
        <div style={{ height: 400, marginTop: 16 }}>
          <DataGrid
            rows={docTypes}
            columns={typeColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            editMode="row"
            processRowUpdate={processTypeRow}
            disableSelectionOnClick
          />
        </div>
      </Box>

      {/* Possessions */}
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1">Possessions</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPossession}
            >
              Add
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSavePossession}
              disabled={!isChangedPossession}
            >
              Save
            </Button>
          </Box>
        </Box>
        <div style={{ height: 400, marginTop: 16 }}>
          <DataGrid
            rows={possessions}
            columns={possessionColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            editMode="row"
            processRowUpdate={processPossessionRow}
            disableSelectionOnClick
          />
        </div>
      </Box>
    </Paper>
  );
}
